import React, { useState, useEffect, useCallback } from 'react';
import { summaryAPI, emailAPI } from '../services/api';
import styles from './Dashboard.module.css';

export default function Dashboard() {
  // weekOffset: 0 = current week, -1 = last week, etc.
  const [weekOffset, setWeekOffset] = useState(0);
  const [totals, setTotals] = useState([]);
  const [overtime, setOvertime] = useState([]);
  const [weekBoundaries, setWeekBoundaries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingCompany, setSendingCompany] = useState(null);
  const [message, setMessage] = useState(null);
  const [lastSend, setLastSend] = useState(null);

  const getDateForOffset = (offset) => {
    const d = new Date();
    d.setDate(d.getDate() + offset * 7);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const date = getDateForOffset(weekOffset);
      const response = await summaryAPI.getWeeklySummary(date);
      setTotals(response.data.totals || []);
      setOvertime(response.data.overtime || []);
      setWeekBoundaries(response.data.weekBoundaries);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  const loadLastSend = useCallback(async () => {
    try {
      const response = await emailAPI.getStatus(getDateForOffset(0));
      setLastSend(response.data.lastEmailSend);
    } catch (err) {
      console.error('Error loading last send:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSummary();
    loadLastSend();
    const interval = weekOffset === 0 ? setInterval(loadSummary, 60000) : null;
    return () => { if (interval) clearInterval(interval); };
  }, [loadSummary, loadLastSend, weekOffset]);

  const handleSendCompanyEmail = async (company) => {
    if (!company.email_recipient) {
      setError(`No email configured for ${company.name}`);
      return;
    }
    if (!window.confirm(`Send report for ${company.name} to ${company.email_recipient}?`)) return;
    try {
      setSendingCompany(company.id);
      await emailAPI.sendCompanyReport(company.id);
      setMessage(`Email sent to ${company.email_recipient} for ${company.name}`);
      loadLastSend();
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setSendingCompany(null);
    }
  };

  const handleSendEmail = async () => {
    if (!window.confirm('Send weekly report to all configured companies?')) return;
    try {
      setSendingEmail(true);
      await emailAPI.sendReport();
      setMessage('Weekly reports sent to all companies!');
      loadLastSend();
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send emails');
    } finally {
      setSendingEmail(false);
    }
  };

  const totalHours = totals.reduce((sum, t) => sum + (t.total_hours || 0), 0).toFixed(2);
  const totalBilling = totals.reduce((sum, t) => sum + (t.billing_amount || 0), 0).toFixed(2);
  const hasBillingRates = totals.some((t) => (t.standard_rate || 0) > 0);

  const formatCurrency = (val) =>
    Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const weekLabel = weekOffset === 0 ? 'Current Week'
    : weekOffset === -1 ? 'Last Week'
    : `${Math.abs(weekOffset)} Weeks Ago`;

  return (
    <div className={styles.container}>
      <h1>Weekly Summary</h1>

      {error && <div className={styles.error}>{error}<button className={styles.dismissBtn} onClick={() => setError(null)}>✕</button></div>}
      {message && <div className={styles.success}>{message}</div>}

      {/* Week Navigation */}
      <div className={styles.weekNav}>
        <button className={styles.weekNavBtn} onClick={() => setWeekOffset((o) => o - 1)}>‹ Prev</button>
        <div className={styles.weekNavCenter}>
          <span className={styles.weekLabel}>{weekLabel}</span>
          {weekBoundaries && (
            <span className={styles.weekDates}>{weekBoundaries.weekStart} — {weekBoundaries.weekEnd}</span>
          )}
        </div>
        <button
          className={styles.weekNavBtn}
          onClick={() => setWeekOffset((o) => o + 1)}
          disabled={weekOffset >= 0}
        >
          Next ›
        </button>
        {weekOffset !== 0 && (
          <button className={styles.weekNavToday} onClick={() => setWeekOffset(0)}>Today</button>
        )}
      </div>

      {loading ? (
        <p className={styles.loading}>Loading...</p>
      ) : weekBoundaries ? (
        <>
          {/* Overtime Alerts */}
          {overtime.length > 0 && weekOffset === 0 && (
            <div className={styles.overtimeAlert}>
              <h3>⚠️ Overtime Alert</h3>
              <ul>
                {overtime.map((ot) => (
                  <li key={ot.id}><strong>{ot.name}</strong>: {ot.total_hours}h</li>
                ))}
              </ul>
            </div>
          )}

          {/* Summary Cards */}
          <div className={styles.totalsGrid}>
            <div className={styles.summaryCard}>
              <h3>Total Hours</h3>
              <p className={styles.largeNumber}>{totalHours}</p>
            </div>
            {hasBillingRates && (
              <div className={styles.summaryCard}>
                <h3>Total Billing</h3>
                <p className={styles.largeNumber}>{formatCurrency(totalBilling)}</p>
              </div>
            )}
            <div className={styles.summaryCard}>
              <h3>Companies</h3>
              <p className={styles.largeNumber}>{totals.length}</p>
            </div>
            <div className={styles.summaryCard}>
              <h3>Entries</h3>
              <p className={styles.largeNumber}>
                {totals.reduce((sum, t) => sum + (t.entry_count || 0), 0)}
              </p>
            </div>
            <div className={styles.summaryCard}>
              <h3>Last Email</h3>
              <p className={styles.largeNumber}>
                {lastSend ? new Date(lastSend).toLocaleDateString() : '—'}
              </p>
            </div>
          </div>

          {/* Company Breakdown */}
          <div className={styles.breakdownSection}>
            <h2>Company Breakdown</h2>
            {totals.length === 0 ? (
              <p>No entries for this week.</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Hours</th>
                    {hasBillingRates && <th>Amount</th>}
                    <th>Entries</th>
                    <th>Status</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {totals.map((total) => (
                    <tr key={total.id}>
                      <td>{total.name}</td>
                      <td className={styles.hours}>
                        {total.total_hours || 0}h
                        {!!total.has_split_rates && (
                          <div className={styles.rateBreakdown}>
                            <span>Std: {total.standard_hours || 0}h</span>
                            <span>Adv: {total.advanced_hours || 0}h</span>
                          </div>
                        )}
                      </td>
                      {hasBillingRates && (
                        <td className={styles.billing}>
                          {(total.standard_rate || 0) > 0
                            ? formatCurrency(total.billing_amount || 0)
                            : <span className={styles.noEmail}>—</span>}
                          {(total.standard_rate || 0) > 0 && !!total.has_split_rates && (
                            <div className={styles.rateBreakdown}>
                              <span>${total.standard_rate}/hr · ${total.advanced_rate}/hr</span>
                            </div>
                          )}
                          {(total.standard_rate || 0) > 0 && !total.has_split_rates && (
                            <div className={styles.rateBreakdown}>
                              <span>${total.standard_rate}/hr</span>
                            </div>
                          )}
                        </td>
                      )}
                      <td>{total.entry_count || 0}</td>
                      <td>
                        {total.total_hours > 40 ? (
                          <span className={styles.overtime}>OVERTIME</span>
                        ) : (
                          <span className={styles.ok}>OK</span>
                        )}
                      </td>
                      <td>
                        {total.email_recipient ? (
                          <button
                            className={styles.btnSend}
                            disabled={sendingCompany === total.id}
                            onClick={() => handleSendCompanyEmail(total)}
                          >
                            {sendingCompany === total.id ? '...' : 'Send'}
                          </button>
                        ) : (
                          <span className={styles.noEmail}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Email Send Section */}
          <div className={styles.emailSection}>
            <h2>Send Reports</h2>
            <div className={styles.emailButtons}>
              <button onClick={handleSendEmail} disabled={sendingEmail} className={styles.btnPrimary}>
                {sendingEmail ? 'Sending...' : 'Send to All Companies'}
              </button>
            </div>
          </div>
        </>
      ) : (
        <p>No data available</p>
      )}
    </div>
  );
}
